import { inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { ConfigService } from '../../services/config/config.service';
import { StorageService } from '../../services/storage/storage.service';
import { ApiService } from '../../services/apiservice/apiservice.service';
import { AuthService } from './../../services/auth/auth.service';
import { LOGIN_STATE, SharedService } from './../../services/shared/shared.service';
import { RoutingService } from '../../services/routing/routing.service';
import { map } from 'rxjs/operators';
import {
  WebSocketHubConnection,
  WebSocketOptions,
  WebSocketService,
} from '../../services/websocket/websocket.service';
import { TokenService } from '../../services/token/token.service';
import { Library } from '../../core/interfaces/library.interface';
import { IllNewConfigSettings } from '../request-edit/Interfaces/IILLNewConfigSettings';

enum WebSocketMessage {
  Start_Lender_List = 'StartLenderList',
  Stop_Lender_List = 'StopLenderList',
  IllLenders = 'IllLenders',
  IllBuildStatus = 'Status',
}

export interface IRequestILL {
  request?: string;
  agControlId: string;
  shard: string;
  format: string;
}

export enum WebSocketStatus {
  Finished = 'Finished',
  Stopped = 'Stopped',
}

export const REQUEST_FORM = {
  ILL_REQUEST: 'illRequest',
  RECORD: 'record',
  OPEN: 'open',
  BLANK: 'blank',
  LOAN: 'loan',
  COPY: 'copy',
  HOLDING_ID: 'holdingId',
  RTI: 'RTI',
  REDIRECT_URL: 'redirect_url',
};

export interface IllNewResponse {
  origRequestInfo?: IllNewRequest;
  showPatronLookup: boolean;
  showILSLookup: boolean;
  showNcipLookup: boolean;
  illPatronStatus: IllPatronStatus;
  copyrightText: string;
  isNonReturnable: boolean;
  doDuplicateCheck: boolean;
  duplicateRequestInfo?: any;
  patronDisclaimer: string;
  showPatronLookupOnForm: boolean;
  returnableFields: IllRequestField[];
  nonReturnableFields: IllRequestField[];
  referenceFields?: IllRequestField[];
  allowChangeNetworkList: boolean;
  minNeedByDays: number;
  heldByMyLibrary: boolean;
  networkIllPickupLocations?: NetworkIllPickupLocationMap;
}

export type NetworkIllPickupLocationMap = Record<string, NetworkIllPickupLocationEntry>;

export interface NetworkIllPickupLocationEntry {
  libraryProfileKey: number;
  pickupLocations: string[];
  defaultPickupLocation: string | null;
}

export interface IllNewRequest {
  requestFormType: string;
  requestType: string;
  forPatronId: number;
  agControlId: string;
  recordId: string;
  multiCopy: boolean;
  materialBibLevel: string;
  isCill: boolean;
  limitOverride: boolean;
  patronOverride: boolean;
  defaultFieldValues: any;
  sourceKey: string;
  requestId: number;
}

export interface IllBuildLenderRequest {
  requestFields: IllRequestField[];
  requestType: string;
}

export interface IllRequestField {
  id: string;
  label: string;
  order: number;
  editable: boolean;
  required: boolean;
  type: string;
  formItems: FormItem[];
}

export interface FormItem {
  idFor: string;
  values: any;
  defaultValue: string;
  minDate: string;
  required: boolean;
  options?: string[];
  editable?: boolean;
  maxLength?: number;
  placeholder?: string;
}

export interface IllSubmitRequest {
  requestFormType: string;
  requestType: string;
  agControlId?: any;
  forPatronId: number;
  multiCopy: boolean;
  confirmation: boolean;
  doCancel: boolean;
  overrideNetworkLenders: boolean;
  networkBibRecordId?: string;
  illSubmitFields: any;
  sourceKey: string;
}

export interface IllSubmitResponse {
  statusText: string;
  networkHandling: IllNetworkHandling;
  requestInfoFields: NameValue[];
}

export interface IllNetworkHandling {
  doNetworkHandle: boolean;
  askForRedirect: boolean;
  doNotPlaceHoldConfirmation: boolean;
  networkRedirectUrl: string;
  networkUserMessage: string;
  networkBibRecordId: string;
  submitRequest: boolean;
  staffOverride: boolean;
}

export interface NameValue {
  name: string;
  value: string;
}

export interface ValidateLenderResponse {
  isValidated: boolean;
  message: string;
}

export interface IllLimitInfo {
  isLimitExceeded: boolean;
  limitExceededMessage: string;
  staffOverride: boolean;
}

export interface IllPatronStatus {
  type: StatusType;
  title: string;
  message: string;
  showOverride: boolean;
}

export interface IllDuplicateResponse {
  isDuplicate: boolean;
  message: string;
  numOfDuplicates: number;
  staffOverride: boolean;
}

export enum StatusType {
  PatronStatus,
  IllLimit,
}

export interface IllLenderList {
  totalTargets: number;
  completedTargets: number;
  updated: boolean;
  results: string[];
}
export interface LenderModel {
  libraryCode: string;
  libraryName: string;
  libraryRegion: string;
  lenderListString: string;
}

export interface IllRequestSearchResponse {
  borrowerUrl: string;
  lenderUrl: string;
  requestId: number;
  title: string;
}
export interface IllRequestDisplay {
  requestId: number;
  status: string;
  progressStatus: string;
  borrower: string;
  lender: string;
  lenderItemBarcode: string;
  requestType: string;
  needByDate: string;
  patronName: string;
  title: string;
  author: string;
  patronID: number;
  reason: string;
  dueDate: string;
  isbn: string;
  format: string;
  requestRecordURL: string;
  borrowerNotes: string;
  lenderNotes: string;
  patronNotes: string;
  volume: string;
  volumesNeeded: string;
  titleSort: string;
}

// Mirrors AGApi.Common.TransferObjects.ILL.IllProgressStatus.Deleted
export const ILL_PROGRESS_STATUS_DELETED = 'del';

export enum RequestBrowseRole {
  Borrower = 'borrower',
  Lender = 'lender',
}

export interface RequestConfig {
  borrower: ConfigNode;
  lender: ConfigNode;
}

export interface ConfigNode {
  title: string;
  sequence?: number;
  libraryProfileKey: number;
  children: ConfigNode[];
  statuses: StatusNode[];
  // helper property to keep track of top-level category for DnD restriction
  categoryKey?: string;
}

export interface StatusNode {
  statusCode: string;
  status: string;
  sequence?: number;
  flag?: number;
}

export interface RequestStatus {
  borrower: RequestStatusGroup[];
  lender: RequestStatusGroup[];
}

export interface RequestStatusGroup {
  title: string;
  statuses: Status[];
}

export type Status = SeparatorItem | StatusItem;

export interface SeparatorItem {
  type: 'separator';
  title: string;
}

export interface StatusItem {
  type: 'status';
  status: RequestManagerStatusDetail;
}

export interface RequestManagerStatusDetail {
  requestType: IllRequestRole;
  status: string;
  count: number;
  url: string;
  requiresAcknowledgements: RequestAcknowledgementDetail[];
}

export enum IllRequestRole {
  Borrower = 'Borrower',
  Lender = 'Lender',
}

export interface IllRequestConfig {
  patronActiveFields: IllEditFormField[];
  patronInActiveFields: IllEditFormField[];
  staffActiveFields: IllEditFormField[];
  staffInActiveFields: IllEditFormField[];
}

export interface IllEditFormField {
  id: string;
  label: string;
  order: number;
  editable: boolean;
  required: boolean;
  type: string;
  agDefaultLabel: string;
  overrideLibrary: boolean;
  canEditLabel: boolean;
  canRemove: boolean;
  canChangeRequired: boolean;
  canOverrideLibrary: boolean;
  formItems: IllEditFormFieldItem[];
}

export interface IllEditFormFieldItem {
  idFor: string;
  values: unknown;
  required: boolean;
}

export interface IllRequestConfigData {
  isCustomerSuperUser: boolean;
  isLibrarySuperUser: boolean;
  copyFormConfig: IllRequestConfig;
  loanFormConfig: IllRequestConfig;
}

export interface ConfigItem {
  id: string;
  label: string;
  agDefaultLabel: string;
  type: string;
}

export interface RequestAcknowledgementDetail {
  requestId: number;
  fields: RequestField[];
}

export interface RequestField {
  field: string;
  changeDate: string;
  acknowledgedDate?: string | null;
  acknowledgedUserId: number;
  requesterLPK: number;
  currentFulfillmentStatus: string;
}

export class RequestService {
  BINDING_LENDER_TIME = 100;
  POLLING_INTERNVAL = 1500;

  // checkboxes states for request form
  readonly copyRightText =
    'WARNING CONCERNING COPYRIGHT RESTRICTIONS: The copyright law of the United States (Title 17, United States Code) governs the making of photocopies or other reproductions of copyrighted material. Under certain conditions specified in the law, libraries and archives are authorized to furnish a photocopy or other reproduction. One of these specified conditions is that the photocopy or other reproduction is not to be "used for any purpose other than private study, scholarship, or research". If a user makes a request for, or later uses, a photocopy or reproduction for purposes in excess of "fair use", that user may be liable for copyright infringement. <BR>This institution reserves the right to refuse to accept a copying order if, in its judgment, fulfillment of the order would involve violation of copyright.  <BR>Click YES if you agree, or Cancel if you do not agree.';
  private readonly _illRequestUrl: string;
  private readonly _webSocket: WebSocketHubConnection;
  private _authService = inject(AuthService);
  private _apiService = inject(ApiService);
  private _configService = inject(ConfigService);
  private _sharedService = inject(SharedService);
  private _storageService = inject(StorageService);
  private _routingService = inject(RoutingService);
  private _tokenService = inject(TokenService);
  private _webSocketService = inject(WebSocketService);

  constructor() {
    this._illRequestUrl = `${this._configService.agApi}/illrequest`;
    this._webSocket = this._webSocketService.newInstance();

    this._sharedService.getLoginStatusBS().subscribe((status) => {
      if (status == LOGIN_STATE.LOGOUT) {
        this._webSocket?.stop().subscribe(() => 'stopping ill websocket');
      } else if (status == LOGIN_STATE.SUCCESSFUL) {
        const connectionOptions: WebSocketOptions = {
          accessTokenFactory: () => {
            return this._tokenService.getResourceSharingToken() ?? this._tokenService.getToken();
          },

          refreshTokenFactory: () => {
            const token =
              this._tokenService.getResourceSharingToken() ?? this._tokenService.getToken();

            return this._authService.refreshToken(token).pipe(
              map((newToken) => {
                const resourceSharingToken = this._tokenService.getResourceSharingToken();
                if (resourceSharingToken) {
                  this._tokenService.setResourceSharingToken(newToken);
                } else {
                  this._tokenService.setToken(newToken);
                }
                return token;
              }),
            );
          },
        };

        this._webSocket
          .start(`${this._configService.agApiWebSocketUrl}/ill`, connectionOptions)
          .subscribe(() => 'started ill websocket');
      }
    });
  }

  async checkILLrequestLimitsAsync(post_request): Promise<any> {
    try {
      const data = await this._authService.getGuestIllReqAsync(post_request);
      if (data !== null) {
        return data;
      } else {
        const messsage = 'unable to submit changes at this moment. Please contact the library';
        this._sharedService.openSnackBar(messsage, 'ok');
      }
    } catch (error) {
      console.error(`request.service.ts:- checkILLrequestLimitsAsync => ${error.message}`);
    }
    return null;
  }

  buildCheckILLRequestLimitsBody(fname = '', lname = '', pass = '') {
    const username = `${lname.trim()}, ${fname.trim()}`; // 61060 - ILL Request Limits not limiting for guest accounts
    return {
      username: username,
      password: pass.trim(),
      sortBy: '',
    };
  }

  unsubscribeToWebSocket(): void {
    this._webSocket.off(WebSocketMessage.IllLenders);
    this._webSocket.off(WebSocketMessage.IllBuildStatus);
  }

  startIllLenderListBuild(
    payload: IllBuildLenderRequest,
    lenderListCallback: () => void,
    statusCallback: () => void,
  ): void {
    this._webSocket.on(WebSocketMessage.IllLenders, lenderListCallback);
    this._webSocket.on(WebSocketMessage.IllBuildStatus, statusCallback);
    this._webSocket.invoke(WebSocketMessage.Start_Lender_List, payload).subscribe(() => {
      console.log('starting build list');
    });
  }

  stopIllLenderListBuild(requestType: string): Observable<void> {
    return this._webSocket.invoke(WebSocketMessage.Stop_Lender_List, requestType);
  }

  lookupILSPatron(data): Observable<any> {
    const url = `${this._illRequestUrl}/LookupILSPatron`;
    return this._apiService.post(url, data);
  }

  getLenders(searchTerm: string): Observable<LenderModel[]> {
    const url = `${this._illRequestUrl}/lenders`;
    return this._apiService.get(url, { params: { searchTerm } });
  }

  doDuplicateCheck(data): Observable<IllDuplicateResponse> {
    const url = `${this._illRequestUrl}/CheckIllRequestDuplicates`;
    return this._apiService.post(url, data).pipe(map((r) => r.data));
  }

  validateLenderList(data): Observable<ValidateLenderResponse> {
    const url = `${this._illRequestUrl}/ValidateLenderList`;
    return this._apiService.post(url, data).pipe(map((r) => r.data));
  }

  updateDefaultPickupLocation(
    userId: number,
    libraryProfileKey: number,
    location: string,
  ): Observable<any> {
    return this._apiService.post(`${this._illRequestUrl}/DefaultPickupLocation`, {
      userId: userId,
      libraryProfileKey: libraryProfileKey,
      location: location,
    });
  }

  updateDefaultNetworkPickupLocation(userId: number, location: string): Observable<any> {
    return this._apiService.post(`${this._illRequestUrl}/DefaultNetworkPickupLocation`, {
      userId: userId,
      location: location,
    });
  }

  newIllRequest(data: IllNewRequest): Observable<IllNewResponse> {
    const url = `${this._illRequestUrl}/new`;

    return this._apiService.post(url, data).pipe(
      map((response) => {
        if (response.data) {
          return response.data;
        } else {
          throw new Error(response.message);
        }
      }),
    );
  }

  submitILLRequest(data: IllSubmitRequest): Observable<IllSubmitResponse> {
    const url = `${this._illRequestUrl}/submit`;
    return this._apiService.post(url, data).pipe(map((r) => r.data));
  }

  search(requestNumber): Observable<IllRequestSearchResponse> {
    const url = `${this._illRequestUrl}/${requestNumber}`;
    return this._apiService.get(url);
  }

  requestBrowse(role: RequestBrowseRole, filter: string = 'all'): Observable<IllRequestDisplay[]> {
    const url = `${this._illRequestUrl}/requestbrowse/${role}`;
    return this._apiService.getWithParams(url, { filter });
  }

  getAcknowledgements(role: RequestBrowseRole): Observable<RequestAcknowledgementDetail[]> {
    const url = `${this._illRequestUrl}/acknowledgements/${role}`;
    return this._apiService.get(url);
  }

  getRequestNotesDefaultLabels(): Observable<Record<string, string>> {
    const url = `${this._illRequestUrl}/requestNotesDefaultLabels`;
    return this._apiService.get<Record<string, string>>(url);
  }

  // Should probably be in libraryService on the core side instead
  getConfigurationLibraries(): Observable<Library[]> {
    const url = `${this._illRequestUrl}/libraries`;
    return this._apiService.get(url).pipe(
      map((response) => {
        if (!response) {
          throwError(() => new Error(response.message));
        }
        return response;
      }),
    );
  }

  recordQueryParams(
    agControlId: string | number,
    shard: string | number,
    format: string,
    sourceKey: string = null,
    multiCopy: boolean = false,
  ) {
    const userData = this._storageService.getUserData();

    return {
      agControlId: agControlId,
      shard: shard,
      format: format,
      isCill: userData.illOptions.hasILL,
      sourceKey: sourceKey,
      multiCopy: multiCopy,
    };
  }

  hasUserIllPermissions(): boolean {
    const userData = this._storageService.getUserData();

    // no permission for CILL to do 'request this item'.
    if (!userData.illOptions.hasILL || !userData.illPermissions) {
      return false;
    }

    if (userData.isStaff) {
      return true;
    }

    const illPermissionsInfo = userData.illPermissions;
    const pRequestToBorrow = illPermissionsInfo.pRequestToBorrow
      ? illPermissionsInfo.pRequestToBorrow
      : false;
    const pRequestForCopy = illPermissionsInfo.pRequestForCopy
      ? illPermissionsInfo.pRequestForCopy
      : false;

    if (pRequestToBorrow || pRequestForCopy) {
      return true;
    }

    return false;
  }

  requestThisItem(
    agControlId: string | number = null,
    shard: string | number = null,
    format: string = null,
    sourceKey: string = null,
    multiCopy: boolean = false,
  ): void {
    const queryParams = this.recordQueryParams(agControlId, shard, format, sourceKey, multiCopy);
    this._routingService.goToIllRequest(queryParams);
  }

  requestThisItemBlank() {
    this.requestThisItem();
    this._routingService.goToBlankIllRequest();
  }

  getRequestStatusConfig(lpk: number): Observable<RequestConfig> {
    const url = `${this._illRequestUrl}/requestmanagerconfiguration/${lpk}`;
    return this._apiService.get(url);
  }

  saveRequestStatusConfig(data: RequestConfig, lpk: number) {
    const url = `${this._illRequestUrl}/requestmanagerconfiguration/${lpk}`;
    return this._apiService.post(url, data);
  }

  deleteRequestStatusConfig(lpk: number) {
    const url = `${this._illRequestUrl}/requestmanagerconfiguration/${lpk}`;
    return this._apiService.delete(url);
  }

  getRequestStatuses(): Observable<RequestStatus> {
    const url = `${this._illRequestUrl}/RequestManagerStatus`;
    return this._apiService.get(url);
  }

  illFormEditGetData(lpk: number): Observable<IllRequestConfigData> {
    const url = `${this._illRequestUrl}/IllFormEditGetData/${lpk}`;
    return this._apiService.get(url).pipe(
      map((result: IllRequestConfigData) => {
        return result;
      }),
    );
  }

  illFormEditSaveData(data: any, lpk: number): Observable<any> {
    const url = `${this._illRequestUrl}/IllFormEditSaveData/${lpk}`;
    return this._apiService.post(url, data);
  }

  illFormEditSaveNewConfig(data: any, lpk: number) {
    const url = `${this._illRequestUrl}/IllFormEditSaveNewConfig/${lpk}`;
    return this._apiService.post(url, data);
  }

  getNewConfig(lpk: number): Observable<IllNewConfigSettings> {
    const url = `${this._illRequestUrl}/IllFormEditGetNewConfig/${lpk}`;
    return this._apiService.get(url);
  }
}
